import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';
import {
	background,
	borderOutline,
	brandPrimaryHoverTint,
	brandPrimaryText,
	darkGrayFontColor,
	destructive,
	errorLight,
	header,
	lightGray,
	midGray,
	success,
	successTint,
	titles,
	warningDark,
	warningLight,
	white,
} from '../../../web/styles/colors';

export const styleSheet = StyleSheet.create({
	container: {
		padding: 24,
		maxWidth: 1000,
		margin: '0 auto',
	},
	section: {
		marginBottom: 32,
	},
	sectionTitle: {
		color: header,
		fontSize: 18,
		fontWeight: 600,
		marginBottom: 16,
	},
	formGroup: {
		marginBottom: 24,
	},
	label: {
		color: header,
		fontSize: 14,
		fontWeight: 600,
		marginBottom: 8,
		display: 'block',
	},
	dateNavigationContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		marginBottom: 8,
	},
	dateNavButton: {
		padding: '8px 16px',
		fontSize: 20,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		color: header,
		minWidth: 48,
		':hover:not(:disabled)': {
			backgroundColor: lightGray,
		},
		':disabled': {
			opacity: 0.3,
			cursor: 'not-allowed',
		},
	},
	dateDisplay: {
		fontSize: 16,
		fontWeight: 600,
		color: header,
		flex: 1,
		textAlign: 'center',
	},
	dateDisplayClickable: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: brandPrimaryText,
		textDecoration: 'underline',
		':hover:not(:disabled)': {
			opacity: 0.8,
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
			color: header,
			textDecoration: 'none',
		},
	},
	todayIndicator: {
		fontSize: 12,
		color: brandPrimaryText,
		fontWeight: 600,
		marginTop: 4,
	},
	button: {
		padding: '10px 24px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: 'none',
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	primaryButton: {
		backgroundColor: brandPrimaryText,
		color: white,
		':hover': {
			opacity: 0.9,
		},
	},
	secondaryButton: {
		backgroundColor: midGray,
		color: header,
		':hover': {
			opacity: 0.9,
		},
	},
	meetingsTable: {
		width: '100%',
		borderCollapse: 'collapse',
		marginTop: 16,
		border: `1px solid ${midGray}`,
	},
	tableHeader: {
		backgroundColor: lightGray,
		borderBottom: `2px solid ${midGray}`,
	},
	tableHeaderCell: {
		padding: 12,
		textAlign: 'left',
		fontWeight: 600,
		fontSize: 14,
		color: header,
	},
	meetingDetailsHeader: {
		maxWidth: 400,
	},
	tableRow: {
		borderBottom: `1px solid ${midGray}`,
		':hover': {
			backgroundColor: background,
		},
	},
	tableCell: {
		padding: 12,
		fontSize: 14,
	},
	meetingDetailsCell: {
		maxWidth: 400,
		wordWrap: 'break-word',
	},
	meetingTitle: {
		fontWeight: 600,
		marginBottom: 4,
	},
	attendeesPreview: {
		fontSize: 12,
		color: darkGrayFontColor,
		marginTop: 4,
	},
	actionButton: {
		padding: '6px 12px',
		fontSize: 12,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${brandPrimaryText}`,
		backgroundColor: white,
		color: brandPrimaryText,
		marginRight: 8,
		':hover': {
			backgroundColor: brandPrimaryText,
			color: white,
		},
	},
	transcriptDialog: {
		maxWidth: 900,
		width: '90vw',
		maxHeight: '80vh',
	},
	transcriptDialogWide: {
		maxWidth: 1100,
		width: 1100,
		maxHeight: '80vh',
	},
	transcriptDialogFullScreen: {
		maxWidth: '95vw',
		width: '95vw',
		maxHeight: '90vh',
	},
	transcriptDialogConfirmation: {
		maxWidth: 1000,
		width: 1000,
		maxHeight: '85vh',
	},
	transcriptDialogBody: {
		overflowY: 'auto',
		maxHeight: 'calc(80vh - 120px)',
		paddingRight: 10,
	},
	transcriptCard: {
		marginBottom: 16,
		padding: 16,
		backgroundColor: background,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
	},
	transcriptCardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: 16,
	},
	transcriptTopic: {
		fontSize: 16,
		fontWeight: 600,
		color: header,
		marginBottom: 8,
	},
	transcriptMeta: {
		fontSize: 13,
		color: darkGrayFontColor,
		marginBottom: 4,
	},
	transcriptReasons: {
		fontSize: 12,
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginTop: 4,
	},
	transcriptLink: {
		background: 'none',
		border: 'none',
		color: brandPrimaryText,
		fontSize: 13,
		fontWeight: 600,
		cursor: 'pointer',
		textDecoration: 'underline',
		padding: 0,
		':hover': {
			opacity: 0.8,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	viewTranscriptLink: {
		background: 'none',
		border: 'none',
		color: darkGrayFontColor,
		fontSize: 12,
		fontWeight: 'normal',
		cursor: 'pointer',
		textDecoration: 'underline',
		padding: 0,
		':hover': {
			opacity: 0.8,
		},
	},
	transcriptCardFooter: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: 12,
		paddingTop: 12,
		borderTop: `1px solid ${midGray}`,
	},
	expandedContent: {
		marginTop: 16,
		paddingTop: 16,
		borderTop: `1px solid ${midGray}`,
	},
	summaryInCard: {
		marginTop: 16,
		padding: 16,
		backgroundColor: brandPrimaryHoverTint,
		borderRadius: 4,
		border: `2px solid ${brandPrimaryText}`,
	},
	summaryTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: header,
		marginBottom: 8,
	},
	summaryText: {
		fontSize: 14,
		lineHeight: 1.6,
		whiteSpace: 'pre-wrap',
	},
	transcriptContent: {
		marginTop: 16,
		padding: 16,
		backgroundColor: white,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		maxHeight: 400,
		overflowY: 'auto',
		whiteSpace: 'pre-wrap',
		fontSize: 13,
		lineHeight: 1.6,
	},
	transcriptContentEditable: {
		width: '100%',
		marginTop: 16,
		padding: 16,
		backgroundColor: white,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		maxHeight: 400,
		overflowY: 'auto',
		whiteSpace: 'pre-wrap',
		fontSize: 13,
		lineHeight: 1.6,
		fontFamily: 'inherit',
		resize: 'vertical',
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
	},
	matchScore: {
		display: 'inline-block',
		padding: '4px 8px',
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 600,
	},
	matchScoreHigh: {
		backgroundColor: successTint,
		color: success,
	},
	matchScoreMedium: {
		backgroundColor: warningLight,
		color: warningDark,
	},
	matchScoreLow: {
		backgroundColor: errorLight,
		color: destructive,
	},
	noDataMessage: {
		padding: 24,
		textAlign: 'center',
		color: header,
		fontSize: 14,
		maxWidth: 600,
		margin: '0 auto',
	},
	errorMessage: {
		padding: 12,
		backgroundColor: errorLight,
		color: destructive,
		borderRadius: 4,
		marginBottom: 16,
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 32,
	},
	userInfo: {
		padding: 12,
		backgroundColor: successTint,
		color: success,
		borderRadius: 4,
		marginBottom: 16,
		fontSize: 14,
	},
	notesRecordedIndicator: {
		padding: '6px 12px',
		backgroundColor: successTint,
		color: success,
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 600,
		display: 'inline-block',
	},
	meetingStatusCell: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 4,
	},
	logAnywayLink: {
		fontSize: 12,
		fontWeight: 600,
		height: 'auto',
		padding: 0,
		textDecoration: 'underline',
	},
	noShowIndicator: {
		padding: '6px 12px',
		backgroundColor: destructive,
		color: white,
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 600,
		display: 'inline-block',
	},
	// Meeting Note Form Styles
	meetingNoteForm: {
		marginTop: 16,
	},
	formTitle: {
		fontSize: 16,
		fontWeight: 600,
		color: header,
		marginBottom: 20,
	},
	formColumns: {
		display: 'grid',
		gridTemplateColumns: '1fr 400px',
		gap: 32,
		alignItems: 'flex-start',
	},
	formLeftColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
	},
	formRightColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		backgroundColor: white,
		padding: 16,
		paddingLeft: 10,
		borderRadius: 4,
	},
	formField: {
		marginBottom: 0,
	},
	formLabel: {
		display: 'block',
		fontSize: 14,
		fontWeight: 600,
		color: header,
		marginBottom: 8,
	},
	required: {
		color: destructive,
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		fontSize: 14,
		color: header,
		cursor: 'pointer',
	},
	richEditorWrapper: {
		border: `1px solid ${midGray}`,
		borderRadius: 4,
		overflow: 'hidden',
	},
	select: {
		width: '100%',
		padding: 10,
		fontSize: 14,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		cursor: 'pointer',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
	},
	selectedAccountDisplay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: 10,
		backgroundColor: successTint,
		borderRadius: 4,
		border: `1px solid ${successTint}`,
	},
	changeAccountButton: {
		padding: '4px 12px',
		fontSize: 12,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${brandPrimaryText}`,
		backgroundColor: white,
		color: brandPrimaryText,
		':hover': {
			backgroundColor: brandPrimaryText,
			color: white,
		},
	},
	formActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 12,
		marginTop: 16,
	},
	cancelButton: {
		padding: '10px 24px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		color: header,
		':hover:not(:disabled)': {
			backgroundColor: lightGray,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	saveButton: {
		padding: '10px 24px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: 'none',
		backgroundColor: brandPrimaryText,
		color: white,
		':hover:not(:disabled)': {
			opacity: 0.9,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	validationErrors: {
		marginBottom: 16,
		padding: 12,
		backgroundColor: errorLight,
		borderRadius: 4,
		border: `1px solid ${errorLight}`,
	},
	validationError: {
		fontSize: 14,
		color: destructive,
		marginBottom: 4,
		':last-child': {
			marginBottom: 0,
		},
	},
	nextMeetingContainer: {
		display: 'flex',
		gap: 8,
		alignItems: 'center',
	},
	nextMeetingButton: {
		flex: 1,
		padding: 10,
		fontSize: 14,
		textAlign: 'left',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		cursor: 'pointer',
		color: header,
		':hover': {
			backgroundColor: lightGray,
		},
	},
	clearDateButton: {
		padding: '8px 16px',
		fontSize: 13,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		color: darkGrayFontColor,
		':hover': {
			backgroundColor: lightGray,
		},
	},
	datePickerContainer: {
		marginTop: 8,
		padding: 16,
		backgroundColor: white,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
	},
	// No Show Dialog Styles
	markNoShowButton: {
		marginLeft: 8,
		padding: '2px 8px',
		fontSize: 11,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 3,
		border: `1px solid ${destructive}`,
		backgroundColor: white,
		color: destructive,
		':hover': {
			backgroundColor: destructive,
			color: white,
		},
	},
	noShowDialog: {
		maxWidth: 500,
		width: '90vw',
	},
	noShowDialogBody: {
		padding: 20,
	},
	noShowFormField: {
		marginBottom: 20,
	},
	noShowFormLabel: {
		display: 'block',
		fontSize: 14,
		fontWeight: 600,
		color: header,
		marginBottom: 8,
	},
	noShowDialogActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 12,
		marginTop: 24,
		paddingTop: 20,
		borderTop: `1px solid ${midGray}`,
	},

	// Web Team Confirmation Styles
	webTeamConfirmation: {
		padding: 32,
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},
	confirmationMessage: {
		textAlign: 'center',
		padding: 24,
		backgroundColor: background,
		borderRadius: 8,
	},
	confirmationTitle: {
		fontSize: 20,
		fontWeight: 600,
		color: titles,
		marginBottom: 16,
	},
	confirmationText: {
		fontSize: 16,
		lineHeight: 1.5,
		color: darkGrayFontColor,
	},
	confirmationActions: {
		display: 'flex',
		gap: 16,
		justifyContent: 'center',
	},
	summaryHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 8,
	},
	summaryNote: {
		fontSize: 13,
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginBottom: 8,
	},
	summaryContent: {
		padding: 16,
		backgroundColor: background,
		borderRadius: 8,
		whiteSpace: 'pre-wrap',
		maxHeight: 400,
		overflowY: 'auto',
		fontFamily: 'monospace',
		fontSize: 13,
		lineHeight: 1.6,
		border: `1px solid ${borderOutline}`,
	},
	summaryActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: 16,
	},
	summaryHeaderActions: {
		display: 'flex',
		gap: 8,
	},
	contentTeamTextarea: {
		width: '100%',
		minHeight: 300,
		padding: 12,
		fontFamily: 'monospace',
		fontSize: 13,
		lineHeight: 1.6,
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		resize: 'vertical',
		backgroundColor: background,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			opacity: 0.7,
			cursor: 'not-allowed',
		},
	},
	contentTeamEditorContainer: {
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		overflow: 'hidden',
		backgroundColor: white,
		// Add border under the TinyMCE toolbar
		':nth-child(1n) .tox-editor-header': {
			borderBottom: `1px solid ${borderOutline}`,
		},
	},
	recapEmailEditorContainer: {
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		overflow: 'hidden',
		backgroundColor: white,
		':nth-child(1n) .tox-editor-header': {
			borderBottom: `1px solid ${borderOutline}`,
		},
	},
	ccNotice: {
		fontSize: 12,
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginTop: 8,
		textAlign: 'right' as const,
	},
	confirmationTextWithMargin: {
		marginBottom: 20,
	},

	// Two-column confirmation layout styles
	confirmationContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: '16px 30px 30px',
	},
	confirmationColumns: {
		display: 'flex',
		gap: 24,
	},
	confirmationColumn: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		minWidth: 0,
	},
	confirmationColumnFull: {
		maxWidth: 'none',
		margin: 0,
	},
	confirmationPanel: {
		padding: 20,
		backgroundColor: background,
		borderRadius: 8,
		border: `1px solid ${borderOutline}`,
	},
	confirmationPanelTitle: {
		fontSize: 16,
		fontWeight: 600,
		color: titles,
		marginBottom: 16,
		marginTop: 0,
	},

	// Deep Research input fields for web team
	deepResearchFields: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		marginBottom: 16,
	},
	deepResearchFieldLabel: {
		display: 'block',
		fontSize: 13,
		fontWeight: 600,
		color: darkGrayFontColor,
		marginBottom: 4,
	},
	deepResearchFieldInput: {
		width: '100%',
		padding: '8px 12px',
		fontSize: 14,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: lightGray,
			cursor: 'not-allowed',
		},
	},
	deepResearchRequired: {
		color: destructive,
	},
	deepResearchScopeTrigger: {
		width: '100%',
	},
	deepResearchScopePlaceholder: {
		color: darkGrayFontColor,
	},
	deepResearchScopeOption: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	deepResearchScopeOptionLabel: {
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.sm,
	},
	deepResearchScopeOptionDescription: {
		fontSize: THEME.typography.fontSizes.xs,
		color: darkGrayFontColor,
		whiteSpace: 'normal',
	},

	// Utility styles to replace inline styles
	loadingText: {
		marginLeft: 16,
	},
	onboardingText: {
		marginLeft: 8,
	},
	transcriptDivider: {
		margin: '0 8px',
	},
	pipeDivider: {
		margin: '0 10px',
		color: darkGrayFontColor,
	},
	fullWidth: {
		flex: 1,
	},
	transcriptPreview: {
		marginBottom: 16,
		padding: 12,
		backgroundColor: background,
		borderRadius: 4,
	},
	transcriptPreviewTitle: {
		fontWeight: 600,
		marginBottom: 4,
	},
	transcriptPreviewContent: {
		fontSize: 13,
		color: darkGrayFontColor,
	},
	// Transcript loading status banners
	warningBanner: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '12px 16px',
		backgroundColor: warningLight,
		color: warningDark,
		borderRadius: 4,
		marginBottom: 16,
		fontSize: 14,
		border: `1px solid ${warningDark}`,
	},
	warningIcon: {
		fontSize: 16,
		flexShrink: 0,
	},
	errorBanner: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 16,
		padding: 24,
		backgroundColor: errorLight,
		color: destructive,
		borderRadius: 4,
		textAlign: 'center',
		border: `1px solid ${destructive}`,
	},
	errorIcon: {
		fontSize: 24,
	},
	errorText: {
		fontSize: 14,
		fontWeight: 500,
	},
	retryButton: {
		padding: '8px 20px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${destructive}`,
		backgroundColor: white,
		color: destructive,
		':hover:not(:disabled)': {
			backgroundColor: destructive,
			color: white,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},

	// Account Profile (AI Custom Content) styles
	accountProfileFields: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		marginBottom: 16,
	},
	accountProfileField: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	accountProfileFieldGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		gap: THEME.spacing.md,
	},
	accountProfileFieldLabel: {
		display: 'block',
		fontSize: '13px',
		fontWeight: 600,
		color: darkGrayFontColor,
		marginBottom: '4px',
	},
	accountProfileFieldInput: {
		width: '100%',
		padding: '8px 12px',
		fontSize: '14px',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: '#f5f5f5',
			cursor: 'not-allowed',
		},
	},
	accountProfileTextarea: {
		width: '100%',
		padding: '10px 12px',
		fontSize: 14,
		lineHeight: 1.6,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		fontFamily: 'inherit',
		resize: 'vertical',
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: background,
			cursor: 'not-allowed',
		},
	},

	// Team Type Selector styles
	teamTypeSelector: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 8,
		marginTop: 0,
		paddingTop: 0,
		marginBottom: 16,
	},
	teamTypeSelectorLabel: {
		fontSize: 14,
		fontWeight: 600,
		color: darkGrayFontColor,
		whiteSpace: 'nowrap' as const,
	},
	teamTypeTrigger: {
		minWidth: 200,
		width: 200,
	},

	// Tab-based confirmation styles
	confirmationTabBar: {
		display: 'flex',
		gap: 4,
		marginBottom: 24,
		borderBottom: `1px solid ${borderOutline}`,
		paddingBottom: 0,
	},
	confirmationTab: {
		padding: '12px 20px',
		fontSize: 14,
		fontWeight: 500,
		color: darkGrayFontColor,
		backgroundColor: 'transparent',
		border: 'none',
		borderBottom: '2px solid transparent',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		marginBottom: -1,
		':hover': {
			color: brandPrimaryText,
			backgroundColor: background,
		},
	},
	confirmationTabActive: {
		color: brandPrimaryText,
		borderBottomColor: brandPrimaryText,
		fontWeight: 600,
	},
	confirmationTabCompleted: {
		color: success,
	},
	confirmationTabContent: {
		minHeight: 400,
	},

	// Recap Email styles
	recipientPreview: {
		padding: '10px 12px',
		backgroundColor: background,
		borderRadius: 4,
		border: `1px solid ${borderOutline}`,
		fontSize: 13,
		color: darkGrayFontColor,
		lineHeight: 1.5,
		wordBreak: 'break-word' as const,
	},
	recapEmailTextarea: {
		width: '100%',
		minHeight: 250,
		padding: 12,
		fontFamily: 'inherit',
		fontSize: 14,
		lineHeight: 1.6,
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		resize: 'vertical' as const,
		backgroundColor: white,
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			opacity: 0.7,
			cursor: 'not-allowed',
		},
	},
});
